import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TokenService } from '../services/tokenService';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Function to test authentication with backend
  const testAuthWithBackend = useCallback(async (token: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setSuccess(`✅ Authentication successful! Welcome, ${userData.user.name || userData.user.email}!`);
        setTimeout(() => {
          navigate('/app');
        }, 2000);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Auth test failed:', error);
      setError(`❌ Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      TokenService.clearToken();
    }
  }, [navigate]);

  useEffect(() => {
    const token = searchParams.get('token');
    const tokenType = searchParams.get('type');
    const error = searchParams.get('error');

    if (error) {
      setError('Authentication failed. Please try again.');
      setTimeout(() => {
        navigate('/login');
      }, 5000);
      return;
    }

    if (token) {
      try {
        if (tokenType === 'google') {
          // This is a Google access token - store it directly
          TokenService.saveToken(token, 24 * 60 * 60); // 24 hours
          console.log('🔑 Google access token received and stored');
          
          // Test authentication with backend
          testAuthWithBackend(token);
        } else {
          // This is a base64 encoded user data (legacy)
          const userData = JSON.parse(atob(token));
          TokenService.saveToken(token, 24 * 60 * 60); // 24 hours
          console.log('🔑 Base64 token received:', userData);
          
          // Test authentication with backend
          testAuthWithBackend(token);
        }
      } catch (err) {
        console.error('Token processing error:', err);
        setError('Failed to process authentication. Please try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } else {
      setError('No authentication token received.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [searchParams, navigate, testAuthWithBackend]);

  return (
    <div className="auth-callback">
      <div className="auth-callback-container">
        <div className="auth-callback-card">
          {error ? (
            <>
              <h2>❌ Authentication Error</h2>
              <p>{error}</p>
            </>
          ) : success ? (
            <>
              <h2>✅ Authentication Successful</h2>
              <p>{success}</p>
              <p>Redirecting to app...</p>
            </>
          ) : (
            <>
              <h2>🔄 Processing Authentication</h2>
              <p>Please wait while we complete your login...</p>
              <div className="loading-spinner"></div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .auth-callback {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f1419 0%, #1a2332 50%, #2d3a4f 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .auth-callback-container {
          width: 100%;
          max-width: 400px;
        }

        .auth-callback-card {
          background: rgba(45, 74, 135, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 40px;
          backdrop-filter: blur(4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          text-align: center;
        }

        .auth-callback-card h2 {
          color: #64c8ff;
          margin-bottom: 20px;
          font-size: 1.5rem;
        }

        .auth-callback-card p {
          color: #ffffff;
          margin-bottom: 15px;
          font-size: 1rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(100, 200, 255, 0.3);
          border-top: 3px solid #64c8ff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 20px auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback; 